/**
 * Navigation guards.
 *
 * A route declares its auth needs through route meta. The default for
 * every flag is `false`, so the auth pages themselves don't need any
 * opt-out.
 */
import type { NavigationGuard } from 'vue-router'

import { useSessionStore } from '@/app/stores/session.store'
import { useSiteStore } from '@/app/stores/site.store'

declare module 'vue-router' {
  interface RouteMeta {
    /** Route requires a signed-in user. Default: false. */
    requiresAuth?: boolean
    /** Route should NOT be visited when already signed in. Default: false. */
    publicOnly?: boolean
    /**
     * Route is scoped to a site (`/site/:siteId/...`). Guard verifies
     * the id in the URL belongs to the user's allowed sites and pins
     * it in the site store on entry.
     */
    requiresSite?: boolean
    /**
     * Route requires the AI menu-photo uploader (`ez-helper` plugin)
     * to be active on the currently-selected site. Absent / false →
     * bounce to dashboard. (The previous `requiresOrdering` guard
     * was retired when ordering moved to the capabilities envelope —
     * OrderingHomeView now handles missing/disabled in-view.)
     */
    requiresAiUploader?: boolean
  }
}

export const requireAuth: NavigationGuard = (to) => {
  const session = useSessionStore()

  if (to.meta.requiresAuth && !session.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }
  if (to.meta.publicOnly && session.isAuthenticated) {
    return { name: 'sites' }
  }
  return true
}

/**
 * Validates `:siteId` in the URL against the session's allowed sites
 * and writes the selection into the site store. Source of truth for
 * "is this site accessible" is `session.sites` — the list the server
 * sent us on login/me. We don't decode the JWT for this; if access has
 * been revoked since the token was issued, the next API call will 403
 * and onAuthLost / route-level handling will deal with it.
 */
export const requireSite: NavigationGuard = (to) => {
  if (!to.meta.requiresSite) return true

  const session = useSessionStore()
  if (!session.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }

  const raw = to.params.siteId
  const id = typeof raw === 'string' ? Number(raw) : NaN
  if (!Number.isInteger(id) || id <= 0) {
    return { name: 'sites' }
  }

  const siteStore = useSiteStore()
  const allowed = session.sites.some((s) => s.id === id)
  if (!allowed) {
    // Either the user isn't a member, or the session list is stale.
    // Either way, /sites is the right place — they can re-pick.
    siteStore.clearSelection()
    return { name: 'sites' }
  }

  // Pin the choice (idempotent if the same id is already selected).
  siteStore.selectSite(id)
  return true
}

/**
 * Gate `/site/:id/ai-uploader` on the per-site AI-uploader feature
 * flag. Absent / `false` means "no plugin, no entry".
 */
export const requireAiUploader: NavigationGuard = (to) => {
  if (!to.meta.requiresAiUploader) return true

  const session = useSessionStore()
  const siteStore = useSiteStore()
  const site = siteStore.currentSite ?? session.sites.find((s) => s.id === siteStore.currentSiteId)
  const active = site?.features?.aiUploader?.active === true
  if (!active) {
    return { name: 'site', params: { siteId: String(siteStore.currentSiteId ?? '') } }
  }
  return true
}
